import {
	DisclosureButton,
	DisclosurePanel,
	Disclosure as HeadlessDisclosure,
} from "@headlessui/react";
import { type Dtos, formatToDollar, pluralize, SuretyBondTermAdjustmentType } from "@inrev/common";
import { Option } from "@inrev/core";
import type { FC, ReactNode } from "react";
import { formatFromISODate } from "../../../utils";
import { cn } from "../../lib/utils";
import { Card, Icon, Text } from "../../primitives";
import { PaymentStatusTag, SignedDollars, TermStatusTag } from "./bondTermDisplay";
import { deriveBondTermsRange } from "./bondTermUtils";

type BondTerm = Dtos.SuretyBondTerm.Get.Response;
type BondTermAdjustment = Dtos.SuretyBondTerm.Adjustment.Get.Response;

export type BondTermsCardProps = {
	terms: readonly BondTerm[];
};

const LEDGER_ROW =
	"grid grid-cols-[minmax(4.5rem,6rem)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.8fr)_minmax(4rem,auto)_0.75rem] items-center gap-x-4 px-4 py-2.5";

const LEDGER_COLUMN_HEADINGS = ["Status", "Invoice #", "Effective Date", "Notes"] as const;

const TermFigure = ({ label, children }: { label: string; children: ReactNode }) => (
	<>
		<Text className="justify-self-end" color="tertiary" type="eyebrow">
			{label}
		</Text>
		<div className="justify-self-end">{children}</div>
	</>
);

const AdjustmentRow = ({ adjustment }: { adjustment: BondTermAdjustment }) => (
	<div className={cn(LEDGER_ROW, "border-t border-bc-subtle")} data-testid="bond-term-adjustment">
		<PaymentStatusTag status={adjustment.paymentStatus} />
		<Text color="secondary" truncate type="body">
			{Option.getOrElse(adjustment.invoiceNumber, "—")}
		</Text>
		<Text color="secondary" truncate type="body">
			{Option.getOrElse(formatFromISODate(adjustment.effectiveDate), "—")}
		</Text>
		<Text color="primary" truncate type="body">
			{SuretyBondTermAdjustmentType.displayName(adjustment.type)}
		</Text>
		<div className="flex justify-end">
			{adjustment.type === SuretyBondTermAdjustmentType.Base ? (
				<Text color="primary" truncate type="body-strong">
					{formatToDollar(adjustment.amount)}
				</Text>
			) : (
				<SignedDollars amount={adjustment.amount} type="body" />
			)}
		</div>
		{adjustment.type === SuretyBondTermAdjustmentType.Adjustment && (
			<Icon
				aria-hidden
				color={adjustment.amount < 0 ? "warning" : "tertiary"}
				size="micro"
				type={adjustment.amount < 0 ? "arrow-small-down" : "arrow-small-up"}
			/>
		)}
	</div>
);

const TermDisclosure = ({ ordinal, term }: { ordinal: number; term: BondTerm }) => (
	<HeadlessDisclosure defaultOpen={term.status === "current"}>
		{({ open }) => (
			<div
				className="overflow-hidden rounded-md border border-bc-default bg-surface-base"
				data-testid={`bond-term-${ordinal}`}
			>
				<DisclosureButton
					className={cn(
						"flex w-full cursor-pointer items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-surface-light focus-visible:outline-none focus-visible:ring focus-visible:ring-inset focus-visible:ring-brand-accent/30",
						open && "bg-surface-light",
					)}
				>
					<div className="flex min-w-0 flex-col gap-2.5">
						<Text color="primary" type="body-strong">
							Term {ordinal}
						</Text>
						<Text color="tertiary" type="caption">
							{term.effectiveDate.slice(0, 4)}
						</Text>
					</div>
					<TermStatusTag term={term} />
					<Text color="tertiary" truncate type="caption">
						{pluralize(term.lineItemCount, "line item", "line items")}
					</Text>
					<div className="ml-auto grid shrink-0 grid-flow-col grid-cols-[7rem_7rem_auto] grid-rows-[auto_auto] items-end gap-x-6 gap-y-1.5 pl-2">
						<TermFigure label="Adjustments">
							<SignedDollars amount={term.adjustmentsTotal} type="body-strong" />
						</TermFigure>
						<TermFigure label="Total Premium">
							<Text className="tabular-nums" color="primary" type="title">
								{formatToDollar(term.totalPremium)}
							</Text>
						</TermFigure>
						<Icon
							aria-hidden
							className="row-span-2 self-center"
							color="tertiary"
							size="mini"
							type={open ? "chevron-up" : "chevron-down"}
						/>
					</div>
				</DisclosureButton>
				<DisclosurePanel>
					<div className={cn(LEDGER_ROW, "border-t border-bc-default py-2")}>
						{LEDGER_COLUMN_HEADINGS.map((heading) => (
							<Text color="tertiary" key={heading} truncate type="eyebrow">
								{heading}
							</Text>
						))}
						<Text className="text-right" color="tertiary" type="eyebrow">
							Amount
						</Text>
					</div>
					{term.adjustments.map((adjustment) => (
						<AdjustmentRow adjustment={adjustment} key={adjustment.id} />
					))}
					<div
						className={cn(LEDGER_ROW, "border-t border-bc-default bg-surface-light")}
						data-testid="bond-term-adjustments-total"
					>
						<div className="col-span-4" />
						<div className="flex justify-end">
							<SignedDollars amount={term.adjustmentsTotal} type="body-strong" />
						</div>
					</div>
				</DisclosurePanel>
			</div>
		)}
	</HeadlessDisclosure>
);

export const BondTermsCard: FC<BondTermsCardProps> = ({ terms }) => {
	const range = deriveBondTermsRange(terms);

	return (
		<Card data-testid="bond-terms-card" elevation="raised">
			<Card.Header className="border-b-0 px-5 py-5">
				<div className="flex min-w-0 items-baseline gap-3">
					<Text color="primary" type="title">
						Bond Term &amp; Premium Ledger
					</Text>
					{Option.isSome(range) && (
						<Text color="tertiary" truncate type="caption">
							{pluralize(range.count, "term", "terms")} · {range.startYear}
							{Option.isSome(range.endYear) &&
								range.endYear !== range.startYear &&
								`–${range.endYear}`}
						</Text>
					)}
				</div>
			</Card.Header>
			<Card.Body className="flex flex-col gap-3 px-5 pb-5 pt-0">
				{terms.map((term, index) => (
					<TermDisclosure key={term.id} ordinal={index + 1} term={term} />
				))}
			</Card.Body>
		</Card>
	);
};
